import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table, { Column } from '../components/Table';
import StatusBadge from '../components/StatusBadge';

type Tab = 'home' | 'payslips' | 'profile-change' | 'policies' | 'memos' | 'password';

const TABS: { key: Tab; label: string }[] = [
  { key: 'home', label: 'Home' },
  { key: 'payslips', label: 'Payslips' },
  { key: 'profile-change', label: 'Request Profile Change' },
  { key: 'policies', label: 'Policies' },
  { key: 'memos', label: 'Memos' },
  { key: 'password', label: 'Change Password' },
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function HomeTab() {
  const [home, setHome] = useState<any>(null);
  useEffect(() => {
    api.get('/ess/home').then((res) => setHome(res.data)).catch(() => {});
  }, []);
  if (!home) return <p className="text-gray-500">Loading...</p>;
  const cards = [
    { label: 'Published Payslips', value: home.publishedPayslips },
    { label: 'Pending Leave', value: home.pendingLeave },
    { label: 'Draft Claims', value: home.draftClaims },
    { label: 'Open Feedback', value: home.openFeedback },
    { label: 'Unread Memos', value: home.unreadMemos },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-lg shadow p-4 text-center">
          <p className="text-2xl font-bold">{c.value}</p>
          <p className="text-xs text-gray-500 mt-1">{c.label}</p>
        </div>
      ))}
      <div className="col-span-2 md:col-span-5 bg-white rounded-lg shadow p-4 flex flex-wrap gap-4">
        <Link className="text-blue-600 hover:underline text-sm" to="/lms/leave-applications">Apply / View Leave</Link>
        <Link className="text-blue-600 hover:underline text-sm" to="/ecs/claims">Apply / View Claims</Link>
        <Link className="text-blue-600 hover:underline text-sm" to="/hr/training">Training</Link>
        <Link className="text-blue-600 hover:underline text-sm" to="/lms/fwa/applications">Flexible Work Arrangement</Link>
      </div>
    </div>
  );
}

function PayslipsTab() {
  const [payslips, setPayslips] = useState<any[]>([]);
  useEffect(() => {
    api.get('/ess/payslips').then((res) => setPayslips(res.data.payslips || [])).catch(() => {});
  }, []);
  const columns: Column<any>[] = [
    { key: 'payslip_number', header: 'Payslip #' },
    { key: 'payslip_month', header: 'Month', render: (r) => new Date(r.payslip_month).toISOString().slice(0, 7) },
    { key: 'net_pay', header: 'Net Pay', render: (r) => Number(r.net_pay).toFixed(2) },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <a className="text-blue-600 hover:underline text-sm" href={`${API_URL}/ess/payslips/${r.id}/pdf`} target="_blank" rel="noreferrer">
          View PDF
        </a>
      ),
    },
  ];
  return <Table columns={columns} rows={payslips} emptyMessage="No published payslips yet" />;
}

function ProfileChangeTab() {
  const [changes, setChanges] = useState<any[]>([]);
  const [form, setForm] = useState({ mobileNumber: '', email: '', address: '', maritalStatus: '' });

  const fetchChanges = async () => {
    const res = await api.get('/ess/profile-changes');
    setChanges(res.data.changes || []);
  };
  useEffect(() => {
    fetchChanges();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const changesPayload = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
    if (Object.keys(changesPayload).length === 0) return alert('Fill at least one field to request a change');
    try {
      await api.post('/ess/profile-change', { changes: changesPayload });
      setForm({ mobileNumber: '', email: '', address: '', maritalStatus: '' });
      fetchChanges();
      alert('Change request submitted for HR approval');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to submit request');
    }
  };

  const columns: Column<any>[] = [
    { key: 'field_name', header: 'Field' },
    { key: 'old_value', header: 'Old' },
    { key: 'new_value', header: 'Requested' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="bg-white rounded-lg shadow p-6 grid grid-cols-2 gap-4">
        <Input label="Mobile Number" value={form.mobileNumber} onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })} />
        <Input label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <Select
          label="Marital Status"
          value={form.maritalStatus}
          onChange={(e) => setForm({ ...form, maritalStatus: e.target.value })}
          options={[
            { value: 'Single', label: 'Single' },
            { value: 'Married', label: 'Married' },
            { value: 'Divorced', label: 'Divorced' },
            { value: 'Widowed', label: 'Widowed' },
          ]}
        />
        <Button type="submit" className="col-span-2">
          Submit Change Request
        </Button>
      </form>
      <Table columns={columns} rows={changes} emptyMessage="No change requests yet" />
    </div>
  );
}

function PoliciesTab() {
  const [policies, setPolicies] = useState<any[]>([]);
  useEffect(() => {
    api.get('/ess/policies').then((res) => setPolicies(res.data.policies || [])).catch(() => {});
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {policies.map((p) => (
        <div key={p.id} className="bg-white rounded-lg shadow p-4">
          <p className="font-semibold">{p.policy_name}</p>
          <p className="text-sm text-gray-600">{p.description}</p>
          {p.policy_file_url && (
            <a className="text-blue-600 hover:underline text-sm" href={p.policy_file_url} target="_blank" rel="noreferrer">
              Download
            </a>
          )}
        </div>
      ))}
      {policies.length === 0 && <p className="text-gray-500">No policies published</p>}
    </div>
  );
}

function MemosTab() {
  const [memos, setMemos] = useState<any[]>([]);
  const fetchMemos = async () => {
    const res = await api.get('/ess/memos');
    setMemos(res.data.memos || []);
  };
  useEffect(() => {
    fetchMemos();
  }, []);
  const markRead = async (id: string) => {
    await api.put(`/ess/memos/${id}/read`);
    fetchMemos();
  };
  return (
    <div className="space-y-3">
      {memos.map((m) => (
        <div key={m.id} className={`bg-white rounded-lg shadow p-4 ${!m.read_at ? 'border-l-4 border-blue-500' : ''}`}>
          <div className="flex justify-between">
            <p className="font-semibold">{m.memo_title}</p>
            {!m.read_at && (
              <button className="text-blue-600 hover:underline text-xs" onClick={() => markRead(m.id)}>
                Mark read
              </button>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">{m.memo_content}</p>
          {m.attachment_url && (
            <a className="text-blue-600 hover:underline text-xs" href={m.attachment_url} target="_blank" rel="noreferrer">
              Attachment
            </a>
          )}
        </div>
      ))}
      {memos.length === 0 && <p className="text-gray-500">No memos</p>}
    </div>
  );
}

function PasswordTab() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.newPassword !== form.confirm) return alert('Passwords do not match');
    try {
      await api.post('/auth/change-password', { currentPassword: form.currentPassword, newPassword: form.newPassword });
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
      alert('Password changed');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to change password');
    }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-lg shadow p-6 max-w-md space-y-4">
      <Input label="Current Password" type="password" value={form.currentPassword} onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} required />
      <Input label="New Password" type="password" value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} required />
      <Input label="Confirm New Password" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required />
      <Button type="submit">Change Password</Button>
    </form>
  );
}

export default function EssPortal() {
  const [tab, setTab] = useState<Tab>('home');

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">My Self-Service</h1>
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'home' && <HomeTab />}
      {tab === 'payslips' && <PayslipsTab />}
      {tab === 'profile-change' && <ProfileChangeTab />}
      {tab === 'policies' && <PoliciesTab />}
      {tab === 'memos' && <MemosTab />}
      {tab === 'password' && <PasswordTab />}
    </div>
  );
}
